/**
 * Client-held bench state — containers, contents, temperature. No server
 * session persistence; see chemistry-lab-target.md's Flow step 1 for why
 * (unscored, low-stakes exploration, same posture as the other sims).
 */
import { findSubstance, mlOf, perAddFor, type Substance } from './substanceCatalog';
import type { Stoichiometry, StoichiometryUnavailable } from '@padcanvas/shared';
import type { ChemistryLabOutcome, TemperatureBand } from './types';
import { nextVesselType, type VesselType } from './vesselTypes';
import { brimVolumeML } from './vesselGeometry';
import { defaultStandFor, type StandType } from './standTypes';
import type { BurnerType } from './burnerTypes';
import { ROOM_C } from './temperature';

/** One entry in a container's running log — "Added Dilute Sulfuric Acid",
 *  "Heated", "Dipped pH Paper" — each carrying the outcome that resulted
 *  from it, if any. This IS the results display now (see StepLog.tsx): a
 *  step with no notable outcome (e.g. adding water to an empty container)
 *  renders as just its label, nothing more to say. */
export interface LabStep {
  id: string;
  label: string;
  /** The same act, said with how much went in — "Added 25 mL of Copper
   *  Sulfate Solution" beside label's "Added Copper Sulfate Solution".
   *
   *  BOTH are stored, rather than one being built from the other, because the
   *  amount is a fact about the act and the sentence is a fact about the act
   *  too: which one is read is the bench's Show quantities setting, and that
   *  can be turned on long after the step happened. Absent for a step that
   *  adds nothing (heating, a dip, a tube). */
  labelWithAmount?: string;
  /** A 'test' asks the mixture a question and its result is the reading; an
   *  'action' changes the mixture and its result is what changed. The step log
   *  reports them differently — see StepLog. */
  kind: 'action' | 'test';
  outcome?: ChemistryLabOutcome;
  source?: 'intrinsic' | 'cache' | 'llm';
  /** The working, on a quantitative bench: moles in, the limiting reagent,
   *  what is left, the yield. Computed by the backend from the equation this
   *  step's outcome carried, and kept ON THE STEP rather than on the container
   *  because it describes one act — the vessel's latest numbers are not the
   *  numbers for the pour before it. Absent on a free bench. */
  stoichiometry?: Stoichiometry;
  /** Why this step has no working, when it has none. */
  stoichiometryUnavailable?: StoichiometryUnavailable;
  /** The burner setting this step's outcome was computed at — lets the step
   *  log tell whether the contents were actually boiling at the time (see
   *  temperature.ts's isBoiling), which `outcome` alone can't say since
   *  boiling compares the mixture's boiling point against the LIVE
   *  temperature, not anything the outcome itself carries. Absent for a
   *  logStep() apparatus-only entry, which has no outcome to begin with. */
  temperatureC?: number;
}

/** How much of one reagent a container holds, in that reagent's own unit
 *  (substanceCatalog.ts's DoseUnit) — 6 drops, 25 mL, 1 g, 3 chips. Adding the
 *  same reagent again raises `qty`; it never makes a second entry, so the ids
 *  in a container are unique. A gas, which is piped in rather than dispensed,
 *  has no dose and carries qty 0. */
export interface VesselContent {
  substanceId: string;
  qty: number;
  /** The molarity this went in at, when the student chose one other than the
   *  catalogue's. Only ever set on the quantitative bench. */
  concentrationM?: number;
  /** Moles, for a content with no dose to convert — a gas that arrived through
   *  a delivery tube, carrying the yield computed for the vessel it came from.
   *  Absent for everything dispensed from the shelf. */
  mol?: number;
}

export interface LabContainer {
  id: string;
  contents: VesselContent[];
  /** What the burner's slider is set to, in °C. The coarse band the chemistry
   *  endpoint takes is DERIVED from this (temperature.ts), never stored — two
   *  copies of the same fact would be two things to keep in step. */
  temperatureC: number;
  outcome?: ChemistryLabOutcome;
  outcomeSource?: 'intrinsic' | 'cache' | 'llm';
  /** The temperature band the current outcome was computed at. The slider is
   *  continuous but the chemistry is banded, so this is what says whether a
   *  new temperature actually needs a new answer. */
  outcomeBand?: TemperatureBand;
  loading: boolean;
  error?: string;
  /** Whether the pH paper has been dipped for the CURRENT contents —
   *  stale (false) the moment contents/temperature change, so a re-test is
   *  never silently shown for a mixture that has since changed. */
  phPaperApplied: boolean;
  /** Which apparatus shape this container is. Defaults round-robin across
   *  the catalogue so a fresh bench already shows the variety — a student
   *  can still change any one container's shape afterward. */
  vesselType: VesselType;
  /** Which stand holds this container. Chosen by the student (standTypes.ts),
   *  seeded from the vessel's default — a round-bottomed vessel has to start
   *  in something, since it can't stand on the bench at all. Two neighbouring
   *  containers both on 'multi-level' share one rod; see benchLayout.ts. */
  standType: StandType;
  /** What is heating this container, if anything (burnerTypes.ts). 'none' by
   *  default — a vessel just sits on the bench until a student puts a flame
   *  under it; heating only makes sense once one is there. */
  burnerType: BurnerType;
  /** Whether a glass rod has been placed in this container, resting across
   *  the rim — the "dip a glass rod in X" apparatus from tests like
   *  ammonia's conc.-HCl fumes test. */
  hasGlassRod: boolean;
  /** What the rod was dipped in before being rested in this container — the
   *  substance itself, not just a colour, because the rod CARRIES a reagent:
   *  the classic test is conc. HCl on a rod held over ammonia. Its own
   *  reagent, never the container's (possibly reacted) mixture. Null when no
   *  rod is resting here. */
  glassRodSubstanceId: string | null;
  /** The container this one's evolved gas is piped into, if a delivery tube
   *  has been run from it. The gas becomes an INPUT to that container's mix
   *  (see GAS_SUBSTANCE_IDS), which is what makes "CO2 into limewater" work
   *  without a second kind of chemistry: it is just another two-substance
   *  mix. Cycles are refused when the tube is connected. */
  pipeToId?: string;
  /** How many times this vessel has been swirled. Kept, rather than being a
   *  transient animation flag, because a swirl leaves the contents ARRANGED
   *  differently: the number seeds the solids' scatter, so the pieces come to
   *  rest somewhere new each time — which is what mixing looks like when what
   *  you are mixing has not dissolved. */
  swirls: number;
  /** The running log of actions taken in this container, oldest first.
   *  Reset whenever the container is cleared — a fresh mixture is a new
   *  experiment, not a continuation of the last one's log. */
  steps: LabStep[];
}

let nextContainerNum = 1;
let nextStepNum = 1;

export function newStepId(): string {
  return `step-${nextStepNum++}`;
}

export function newContainer(vesselType: VesselType): LabContainer {
  return {
    id: `container-${nextContainerNum++}`,
    contents: [],
    temperatureC: ROOM_C,
    loading: false,
    phPaperApplied: false,
    vesselType,
    standType: defaultStandFor(vesselType),
    burnerType: 'none',
    hasGlassRod: false,
    glassRodSubstanceId: null,
    swirls: 0,
    steps: [],
  };
}

export function initialContainers(quantitative = false): LabContainer[] {
  // The quantitative bench opens on a BEAKER, and this is a calibration rather
  // than a preference. A dose is written at test-tube scale and a pour scales
  // x10 in larger glassware while a solid's dose does not, so the same spatula
  // of carbonate meets 1 mL of acid in a tube and 10 mL in a beaker: marble
  // chips and acid start 30x off the ratio in a tube and 3x off in a beaker.
  // A tube puts every solid so far past the ratio that nothing the student can
  // do on the bench brings it back — thirty pours, or 2 M for fifteen times
  // over. See chemistry-lab-quantitative-mode-target.md, "It opens on a
  // beaker".
  return [newContainer(quantitative ? 'beaker' : nextVesselType(0))];
}

/** What is in a container, as the ids alone — which is all the chemistry ever
 *  sees. Quantities stay on the client by design: they are not in the mix key,
 *  so they must not reach the prompt either, or a cached narration would
 *  describe an amount the student did not add. See
 *  chemistry-lab-dosing-target.md. */
export function substanceIdsOf(container: { contents: VesselContent[] }): string[] {
  return container.contents.map(c => c.substanceId);
}

/** Adds one helping of a reagent, at the dose its metadata declares and the
 *  scale the glassware implies. A repeat add adds the same amount again. */
export function addToContents(
  contents: VesselContent[], substanceId: string, vesselType: VesselType,
  /** The strength this helping went in at, where the student chose one. */
  concentrationM?: number,
): VesselContent[] {
  const substance = findSubstance(substanceId);
  const qty = substance ? perAddFor(substance, vesselType) : 0;
  const held = contents.find(c => c.substanceId === substanceId);
  if (!held) return [...contents, { substanceId, qty, ...(concentrationM ? { concentrationM } : {}) }];
  return contents.map(c => {
    if (c.substanceId !== substanceId) return c;
    const total = c.qty + qty;
    // No strength chosen (the free bench, or a reagent with no choice to make):
    // the amount grows and nothing else about it changes.
    if (concentrationM === undefined) return { ...c, qty: total };
    // Two strengths poured into one vessel MIX, and the moles are what is
    // conserved: 10 mL of 1 M and 10 mL of 2 M is 30 mmol in 20 mL — 1.5 M,
    // a strength nobody selected and the one that is actually in the beaker.
    // Recording the last choice instead (as this once did) claimed 40 mmol,
    // which is wrong arithmetic in the one mode whose whole job is arithmetic.
    const before = c.concentrationM ?? substance?.concentrationM ?? concentrationM;
    return { ...c, qty: total, concentrationM: (c.qty * before + qty * concentrationM) / total };
  });
}

/** The substances a container holds, resolved, in the order they went in.
 *  Anything the catalogue no longer knows is dropped rather than drawn. */
export function heldSubstances(contents: VesselContent[]): Substance[] {
  return contents.map(c => findSubstance(c.substanceId)).filter((s): s is Substance => !!s);
}

/** How much LIQUID a vessel is holding, in millilitres. Drops count, at 20 to
 *  the millilitre; solids do not — a spatula of salt sinks into the water
 *  rather than raising it — and a gas is not poured in at all. */
export function liquidVolumeML(contents: VesselContent[]): number {
  return contents.reduce((sum, held) => {
    const substance = findSubstance(held.substanceId);
    return sum + (substance ? (mlOf(substance, held.qty) ?? 0) : 0);
  }, 0);
}

/** Whether this vessel can take another helping of a reagent without going
 *  over the brim. A vessel that is full refuses rather than silently capping:
 *  quietly dropping what a student poured is the one behaviour that teaches
 *  nothing. Anything with no volume of its own (a solid, a gas) always fits —
 *  the level is not what it changes. */
export function hasRoomFor(container: { contents: VesselContent[]; vesselType: VesselType },
  substanceId: string): boolean {
  const substance = findSubstance(substanceId);
  if (!substance) return true;
  const addingML = mlOf(substance, perAddFor(substance, container.vesselType));
  if (addingML === null) return true;
  return liquidVolumeML(container.contents) + addingML <= brimVolumeML(container.vesselType);
}

/** Normalizes to the same key shape the backend uses (for display/debugging
 *  only — the backend recomputes its own key server-side). */
export function mixKeyFor(substanceIds: string[], temperatureBand: string): string {
  return [...new Set(substanceIds)].sort().join('+') + '@' + temperatureBand;
}
